// Display formatting - kept centralised so locale + unit decisions
// happen in one place. Dutch number format throughout.

import type { Live } from "./api";

const NL = new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 1 });
const NL0 = new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 0 });

/** W → "x.x kW" if |W| ≥ 1000, otherwise "n W". */
export function formatPower(watts: number): string {
  const w = Math.round(watts);
  if (Math.abs(w) >= 1000) {
    return `${NL.format(w / 1000)} kW`;
  }
  return `${NL0.format(w)} W`;
}

/** kWh → "x.x kWh" */
export function formatEnergy(kwh: number): string {
  return `${NL.format(kwh)} kWh`;
}

const NL_PRICE = new Intl.NumberFormat("nl-NL", {
  style: "currency",
  currency: "EUR",
  // 3 decimals: NL day-ahead prices are usually -0.03..0.30 EUR/kWh.
  // 2 decimals would render -0.001 EUR as -0,00 - lose the sign.
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

/** EUR/kWh → "€ 0,123 / kWh" (NL formatting). */
export function formatPricePerKwh(eur: number): string {
  return `${NL_PRICE.format(eur)} / kWh`;
}

const NL_EUR = new Intl.NumberFormat("nl-NL", {
  style: "currency",
  currency: "EUR",
  // Round to cents for tile display - sub-cent precision is noise at
  // the day/month/year scale where the earnings tile shows totals.
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** EUR amount → "€ 12,34" (NL formatting, 2 decimals, currency symbol). */
export function formatEur(eur: number): string {
  return NL_EUR.format(eur);
}

/** 0..100 → "n %" */
export function formatPercent(pct: number): string {
  return `${NL0.format(Math.max(0, Math.min(100, pct)))} %`;
}

const NL_TIME = new Intl.DateTimeFormat("nl-NL", {
  hour: "2-digit",
  minute: "2-digit",
});
const NL_DATETIME = new Intl.DateTimeFormat("nl-NL", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

/** ISO datetime → "14:32" in the device's local tz. Used by the
 *  Apparaatstatus card's "online sinds HH:MM". Falls back to "-" on a
 *  null/unparseable input so a missing `since` never renders "NaN". */
export function formatClock(iso: string | null | undefined): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return NL_TIME.format(d);
}

/** ISO datetime → "23 mei 14:32" - used when "sinds HH:MM" alone is
 *  ambiguous (a device last seen on a previous day). */
export function formatDateTimeShort(iso: string | null | undefined): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return NL_DATETIME.format(d);
}

/** Greeting picker - local time on the device, simple bucketing. */
export function greeting(d: Date = new Date()): string {
  const h = d.getHours();
  if (h < 6)  return "Goedenacht";
  if (h < 12) return "Goedemorgen";
  if (h < 18) return "Goedemiddag";
  return "Goedenavond";
}

/** Derive a human label for the battery's current state.
 *
 * The "Charging from solar · 2.1 kW" pill on the inspiration mockups
 * captures what the customer actually wants to know: where the
 * battery's juice is coming from / going to RIGHT NOW. We synthesise
 * it from the signs of pv, battery, grid - small dead-band keeps
 * "Inactief" stable when a fridge cycle nudges battery_w by ±50 W.
 */
const DEAD_BAND_W = 100;
export function batteryStateLabel(live: Live): {
  label: string;
  hint: string;
  tone: "accent" | "sun" | "grid" | "mute";
} {
  const { battery_w, pv_w, grid_w } = live;
  if (battery_w > DEAD_BAND_W) {
    // Charging. Where's the energy coming from?
    if (pv_w > Math.abs(battery_w)) {
      return {
        label: "Opladen vanaf zon",
        hint: formatPower(battery_w),
        tone: "sun",
      };
    }
    if (grid_w > DEAD_BAND_W) {
      return {
        label: "Opladen vanaf net",
        hint: formatPower(battery_w),
        tone: "grid",
      };
    }
    return { label: "Opladen", hint: formatPower(battery_w), tone: "accent" };
  }
  if (battery_w < -DEAD_BAND_W) {
    return {
      label: "Ontladen",
      hint: formatPower(Math.abs(battery_w)),
      tone: "accent",
    };
  }
  return { label: "Inactief", hint: "", tone: "mute" };
}
